import React, { FC } from "react";
import { Box, Label, Text } from "@primer/react";
import { AlertIcon } from "@primer/octicons-react";

// Invitation status metadata — the counterpart to RegistrationStatusContent's
// STATUS_LABELS, kept separate because invitations have their own lifecycle
// (pending → delivering → sent → accepted → registered, or declined).
export const INVITATION_STATUS_LABELS: Record<
  string,
  { label: string; variant: "success" | "attention" | "danger" | "accent" | "done" | "default" }
> = {
  pending: { label: "Pending", variant: "attention" },
  delivering: { label: "Delivering", variant: "accent" },
  sent: { label: "Sent", variant: "success" },
  accepted: { label: "Accepted", variant: "success" },
  declined: { label: "Declined", variant: "danger" },
  registered: { label: "Registered", variant: "done" },
};

// Sendable statuses — keep in sync with BulkActions::InvitationSendExecutor::SENDABLE.
export const SENDABLE_STATUSES = ["pending", "sent", "accepted"];

const sum = (counts: Record<string, number> | undefined, keys: string[]) =>
  keys.reduce((total, key) => total + (counts?.[key] ?? 0), 0);

// A "pending" invitation has never been emailed, so it is a first send; "sent"
// and "accepted" have, so re-sending is a resend. The split drives the mixed
// copy ("3 will be sent, 12 resent").
export const firstSendCount = (counts?: Record<string, number>) => sum(counts, ["pending"]);
export const resendCount = (counts?: Record<string, number>) => sum(counts, ["sent", "accepted"]);
export const sendableCount = (counts?: Record<string, number>) => sum(counts, SENDABLE_STATUSES);

interface InvitationStatusBreakdownProps {
  counts: Record<string, number>;
  describeRow: (status: string) => string;
}

// Unlike the registration StatusBreakdown (built around a single target
// status), invitation actions can act on several statuses at once, so this
// simply describes every non-empty status with the caller's copy.
export const InvitationStatusBreakdown: FC<InvitationStatusBreakdownProps> = ({ counts, describeRow }) => {
  const entries = Object.entries(counts).filter(([, count]) => count > 0);
  if (entries.length <= 1) return null;

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid var(--borderColor-muted, #d1d9e0b3)",
        backgroundColor: "var(--bgColor-muted, #f6f8fa)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mx: 3,
          mt: 3,
          mb: 2,
          px: "12px",
          py: "10px",
          borderRadius: "6px",
          backgroundColor: "var(--bgColor-attention-muted, #fff8c5)",
          border: "1px solid var(--borderColor-attention-muted, #d4a72c66)",
        }}
      >
        <Box sx={{ color: "var(--fgColor-attention, #9a6700)", flexShrink: 0, display: "flex" }}>
          <AlertIcon size={16} />
        </Box>
        <Text fontSize={1} fontWeight="semibold">
          This selection contains invitations with varying statuses
        </Text>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, px: 3, pb: 3 }}>
        {entries.map(([status, count]) => {
          const config = INVITATION_STATUS_LABELS[status] || { label: status, variant: "default" as const };

          return (
            <Box
              key={status}
              sx={{
                px: 3,
                py: 2,
                borderRadius: "6px",
                backgroundColor: "var(--bgColor-default, #ffffff)",
                border: "1px solid var(--borderColor-muted, #d1d9e0b3)",
              }}
            >
              <Text as="div" fontSize={1} sx={{ mb: 1 }}>
                <Text as="span" fontWeight="bold">{count} {count === 1 ? "invitation" : "invitations"}</Text>
                {" "}currently{" "}
                <Label variant={config.variant}>{config.label}</Label>
              </Text>
              <Text as="div" fontSize={1} sx={{ color: "var(--fgColor-muted, #656d76)" }}>
                {describeRow(status)}
              </Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
